import { generatePath } from '@toggle/helpers';
import { useNavigate } from 'react-router';

import { useQueryParams } from '~/hooks/use-query-params/useQueryParams';
import { appPaths } from '~/routes/app-paths';
import { queryKeys, queryValues } from '~/routes/query-params';
import { useSubscription } from '~/stores/use-subscription/useSubscription';
import { create } from '~/utils/store/createStore';

const modes = ['quickAnalysis', 'document', 'research'] as const;
export type ChatMode = typeof modes[number];

type ChatModeStore = {
  chatMode: ChatMode;
  previousMode: ChatMode | null;
  setChatMode: (chatMode: ChatMode) => void;
  resetPreviousMode: () => void;
};

const useChatModeStore = create<ChatModeStore>(set => ({
  chatMode: 'research',
  previousMode: null,
  setChatMode: chatMode =>
    set(state => ({
      previousMode: state.chatMode,
      chatMode,
    })),
  resetPreviousMode: () => set({ previousMode: null }),
}));

export const useChatNavigation = () => {
  const { chatMode, previousMode, setChatMode, resetPreviousMode } =
    useChatModeStore();
  const { params, setQueryParams } = useQueryParams();
  const navigate = useNavigate();
  const isBasicUser = useSubscription(state => state.isBasicUser());

  const navigateToAlfred = (
    mode: ChatMode,
    searchParams = new URLSearchParams()
  ) => {
    setMode(mode);

    navigate({
      pathname: appPaths.alfred,
      search: searchParams.toString(),
    });
  };

  const setMode = (mode: ChatMode) => {
    const onAlfredPage = appPaths.alfred === location.pathname;

    if (onAlfredPage) {
      setQueryParams({ [queryKeys.mode]: mode }, { replace: true });
    }

    setChatMode(mode);
  };

  const navigateToConversation = (conversationId: string, newTab = false) => {
    const url = generatePath({
      basePath: appPaths.alfred,
      params: {
        [queryKeys.mode]: queryValues.deepResearchMode,
        [queryKeys.conversationId]: conversationId,
      },
    });

    if (newTab) {
      window.open(url, '_blank', 'noopener,noreferrer');
      return;
    }

    navigate(url);
  };

  const getMode = (): ChatMode => {
    const paramMode = params?.[queryKeys.mode] as ChatMode | undefined;

    if (paramMode) {
      return paramMode;
    }

    if (chatMode !== 'research') {
      return chatMode;
    }

    return isBasicUser ? 'quickAnalysis' : 'research';
  };

  return {
    navigateToAlfred,
    mode: getMode(),
    previousMode,
    setMode,
    navigateToConversation,
    resetPreviousMode,
  };
};
